/**
 * Which aeroplane the camera is watching.
 *
 * The original's playback bar split this into two columns: `YOURS`
 * (`COCKPIT` / `EXTERNAL`) and `OTHERS` (`SWITCH` / `INDEPENDENT`), with
 * `SWITCH` documented as "Continually pressing SWITCH will cycle you through
 * all the external views of all enemy airplanes in the recorded mission" and
 * both `OTHERS` buttons greyed out until `EXTERNAL` was chosen. That gating is
 * a fact about the camera rather than about the recording — you cannot sit in
 * someone else's cockpit — and it is kept here.
 *
 * The ring is the player first, then everyone else in roster order. Roster
 * order rather than "nearest" or "most interesting" because it is *stable*: an
 * ordering that changed as aeroplanes manoeuvred would make SWITCH land
 * somewhere different every press, and a viewer trying to follow one particular
 * Albatros would lose it.
 *
 * Pure, and unaware of three.js, so the ring's behaviour at the awkward moments
 * — an empty sky, a target that has just been shot down, a tape scrubbed back
 * before an aeroplane existed — is an ordinary unit test rather than something
 * discovered while watching a replay.
 */

/** `null` is the player's own aeroplane; a string is another aircraft's id. */
export type CameraTarget = string | null;

/**
 * Everyone who can be watched, in a stable order: the player, then the others
 * as the roster holds them. Aircraft that are already down stay in the ring —
 * a burning wreck on its way to the ground is one of the things a Mission
 * Recorder exists to let you watch.
 */
export function viewRing(otherIds: readonly string[]): readonly CameraTarget[] {
  return [null, ...otherIds];
}

/**
 * The next aeroplane along the ring, wrapping. A target that has vanished from
 * the roster since it was chosen (a tape scrubbed back past its spawn) restarts
 * the cycle at the player rather than trapping SWITCH on a name that is not
 * there.
 */
export function nextViewTarget(current: CameraTarget, otherIds: readonly string[]): CameraTarget {
  const ring = viewRing(otherIds);
  const index = ring.indexOf(current);
  if (index < 0) return null;
  return ring[(index + 1) % ring.length] ?? null;
}

/**
 * What the bar prints. The player's own aeroplane keeps the original's wording
 * for the state it describes — `CHASING YOUR PLANE` is a verbatim `RB.EXE`
 * string — and another aircraft is named by its callsign.
 */
export function viewTargetLabel(target: CameraTarget, callsignOf: (id: string) => string | undefined): string {
  if (target === null) return "CHASING YOUR PLANE";
  return callsignOf(target) ?? target;
}

/**
 * Whether a target is still in the sky to be watched. The caller re-checks this
 * every frame rather than trusting the id it stored, because a scrub can move
 * the roster underneath a chosen target in either direction.
 */
export function targetIsPresent(target: CameraTarget, otherIds: readonly string[]): boolean {
  return target === null || otherIds.includes(target);
}
